import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Users, RefreshCw } from 'lucide-react';
import { colors } from '../../constants/theme';
import { matchesService } from '../../services/api';

const UpcomingMatches = () => {
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadUpcomingMatches();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(loadUpcomingMatches, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadUpcomingMatches = async () => {
    try {
      console.log('🔄 Carregando próximas partidas...');
      const response = await matchesService.getQueue();
      
      if (response.success) {
        // Pegar apenas as próximas 5 partidas
        const upcoming = (response.data || []).slice(0, 5);
        setUpcomingMatches(upcoming);
        setLastUpdated(new Date());
        console.log('📋 Próximas partidas:', upcoming);
      } else {
        console.warn('⚠️ Erro ao carregar fila:', response.error);
        setUpcomingMatches([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar próximas partidas:', error);
      // Para espectadores, não mostrar erro técnico
      setUpcomingMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const formatQueuePosition = (index) => {
    const positions = ['1ª', '2ª', '3ª', '4ª', '5ª'];
    return positions[index] || `${index + 1}ª`;
  };

  const getPhaseDisplayName = (phase) => {
    const phases = {
      'grupos': 'Fase de Grupos',
      'quartas': 'Quartas de Final',
      'semifinal': 'Semifinal',
      'final': 'Grande Final',
      'terceiro': 'Disputa de Terceiro'
    };
    return phases[phase] || phase;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Carregando próximas partidas...</span>
        </div>
      </div>
    );
  }

  if (upcomingMatches.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            Próximas Partidas
          </h3>
          <p className="text-gray-500">
            Nenhuma partida programada no momento
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Aguarde a organização definir a sequência
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b" style={{ backgroundColor: colors.primary }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Próximas Partidas
          </h3>
          <div className="text-white text-sm opacity-90">
            {upcomingMatches.length} na fila
          </div>
        </div>
      </div>

      {/* Lista de partidas */}
      <div className="divide-y divide-gray-200">
        {upcomingMatches.map((match, index) => (
          <div key={match.id} className="p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              {/* Posição na fila */}
              <div className="flex-shrink-0">
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    Próxima
                  </span>
                  <span 
                    className="text-lg font-bold"
                    style={{ color: colors.primary }}
                  >
                    {formatQueuePosition(index)}
                  </span>
                </div>
              </div>

              {/* Detalhes da partida */}
              <div className="flex-1 mx-4">
                <div className="flex items-center justify-center space-x-4">
                  {/* Jogador 1 */}
                  <div className="text-center flex-1">
                    <p className="font-semibold text-gray-800 truncate">
                      {match.player1?.name || 'Jogador 1'}
                    </p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      {match.player1?.category || 'N/A'}
                    </p>
                  </div>

                  {/* VS e Fase */}
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-400">VS</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getPhaseDisplayName(match.phase)}
                    </p>
                  </div>

                  {/* Jogador 2 */}
                  <div className="text-center flex-1">
                    <p className="font-semibold text-gray-800 truncate">
                      {match.player2?.name || 'Jogador 2'}
                    </p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      {match.player2?.category || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Indicador de prioridade (se houver) */}
              {(match.priority || 0) > 0 && (
                <div className="flex-shrink-0">
                  <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                    Prioridade
                  </div>
                </div>
              )}
            </div>

            {/* Destaque para a primeira partida */}
            {index === 0 && (
              <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                <div className="flex items-center justify-center text-sm text-blue-700">
                  <Users className="h-4 w-4 mr-1" />
                  <span className="font-medium">Próxima a ser iniciada</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer com info de atualização */}
      <div className="px-4 py-3 bg-gray-50 border-t">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Atualização automática a cada 30s</span>
          {lastUpdated && (
            <span>
              Última atualização: {lastUpdated.toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingMatches;